/**
 * Several accounts on one device, the way one person has a work address and a
 * personal one.
 *
 * Each profile is a *separate VAV account*: its own username, its own identity
 * key, its own history. They share nothing but the browser they live in. That
 * is what makes the model easy to reason about — there is no "sub-account", no
 * shared roster, and switching cannot leak one profile's session into another.
 *
 * ## Where the data lives
 *
 * One IndexedDB database per profile. The store is injected into the client
 * already (`createStore`), so isolation costs a database name and nothing else:
 * client-core does not know profiles exist.
 *
 * ## The migration that must not go wrong
 *
 * Existing users have an account in the database literally called `vav`. There
 * is no account recovery in this product — if the first profile pointed at a
 * new, empty database, every existing user would open the app to a registration
 * screen with their real account unreachable. So the first profile keeps the
 * legacy name forever, and only profiles created afterwards get a suffix. The
 * id of the first profile is therefore load-bearing; do not renumber it.
 */

const REGISTRY_KEY = 'vav.profiles';
const LEGACY_PROFILE_ID = 'default';

export interface Profile {
  id: string;
  /** What the user calls this one. Not the account's display name. */
  label: string;
}

interface Registry {
  profiles: Profile[];
  activeId: string;
}

/**
 * The database backing a profile.
 *
 * `default` maps to the bare name every pre-profiles install already uses.
 * Changing this function orphans accounts.
 */
export function databaseNameFor(profileId: string): string {
  return profileId === LEGACY_PROFILE_ID ? 'vav' : `vav:${profileId}`;
}

function fallback(): Registry {
  return { profiles: [{ id: LEGACY_PROFILE_ID, label: '' }], activeId: LEGACY_PROFILE_ID };
}

export function loadRegistry(): Registry {
  try {
    const raw = localStorage.getItem(REGISTRY_KEY);
    if (!raw) return fallback();
    const parsed = JSON.parse(raw) as Registry;
    if (!Array.isArray(parsed.profiles) || parsed.profiles.length === 0) return fallback();
    // A registry naming an active profile that no longer exists would leave the
    // app pointed at a database nobody can reach.
    if (!parsed.profiles.some((p) => p.id === parsed.activeId)) {
      return { profiles: parsed.profiles, activeId: parsed.profiles[0].id };
    }
    return parsed;
  } catch {
    // Corrupt registry: fall back to the legacy profile rather than to an empty
    // one, so the worst case is losing the *list*, never an account.
    return fallback();
  }
}

function save(registry: Registry): void {
  localStorage.setItem(REGISTRY_KEY, JSON.stringify(registry));
}

export function listProfiles(): Profile[] {
  return loadRegistry().profiles;
}

export function activeProfileId(): string {
  return loadRegistry().activeId;
}

/** Adds a profile and makes it active. The caller reloads into it. */
export function createProfile(label: string): Profile {
  const registry = loadRegistry();
  const profile: Profile = { id: crypto.randomUUID().slice(0, 8), label: label.trim() };
  save({ profiles: [...registry.profiles, profile], activeId: profile.id });
  return profile;
}

export function setActiveProfile(id: string): void {
  const registry = loadRegistry();
  if (!registry.profiles.some((p) => p.id === id)) return;
  save({ ...registry, activeId: id });
}

export function renameProfile(id: string, label: string): void {
  const registry = loadRegistry();
  save({
    ...registry,
    profiles: registry.profiles.map((p) => (p.id === id ? { ...p, label: label.trim() } : p)),
  });
}

/**
 * Forget a profile and destroy its data.
 *
 * Irreversible in the strongest sense the product has: the identity key in that
 * database is the only copy anywhere, so this deletes the account itself, not a
 * local cache of it. Callers must confirm before calling.
 */
export async function removeProfile(id: string): Promise<void> {
  const registry = loadRegistry();
  // Refuse to leave the app with no profile at all.
  if (registry.profiles.length <= 1) return;

  const profiles = registry.profiles.filter((p) => p.id !== id);
  const activeId = registry.activeId === id ? profiles[0].id : registry.activeId;
  save({ profiles, activeId });

  await new Promise<void>((resolve) => {
    const request = indexedDB.deleteDatabase(databaseNameFor(id));
    // Resolve on every outcome: the registry entry is already gone, and a
    // blocked delete (another tab holding the database open) must not leave the
    // caller hanging.
    request.onsuccess = () => resolve();
    request.onerror = () => resolve();
    request.onblocked = () => resolve();
  });
}
